import { z } from "zod/v4";
import {
  AGENT_SCOPES,
  AgenticSettingsReviewOpType,
  PROPOSED_ENTITY_KEY_PATTERN,
} from "../../types";

// SCHEMA ONLY. packages/app compiles this through the llm/schema barrel, so keep it free of
// database, storage, and other node-only imports.

// Every dimension of where a definition applies. A proposed scope replaces all of them, so an
// agent that changes one copies the others from the entry's Scope line.
const definitionScopeSchema = z.object({
  parent_model_ids: z.array(z.number().int()),
  source_ids: z.array(z.number().int()),
  // Null applies to every agent.
  agent_scopes: z.array(z.enum(AGENT_SCOPES)).nullable(),
  // Null applies to every brand profile.
  brand_profile_ids: z.array(z.string().min(1)).nullable(),
});

export type DefinitionScopeOperation = z.infer<typeof definitionScopeSchema>;

const wireDefinitionScopeSchema = z
  .object({
    parent_model_ids: definitionScopeSchema.shape.parent_model_ids.describe(
      "The parent models it applies to, by the IDs in Scope lines. Empty, with source_ids empty too, for the whole workspace.",
    ),
    source_ids: definitionScopeSchema.shape.source_ids.describe(
      "The data sources it applies to, by the IDs in Scope lines. Empty, with parent_model_ids empty too, for the whole workspace.",
    ),
    agent_scopes: definitionScopeSchema.shape.agent_scopes.describe(
      "The agents it applies to (ads, lcm, data). Null for every agent.",
    ),
    brand_profile_ids: definitionScopeSchema.shape.brand_profile_ids.describe(
      "The brand profiles it applies to, by ID. Null for every brand profile.",
    ),
  })
  .describe(
    "Where the definition applies. Replaces every dimension of the current scope, so copy the ones you are not changing from the entry's Scope line.",
  );

const nameSchema = z.string().min(1);
const markdownSchema = z.string().min(1);
// Omitted keeps the definition's current description.
const descriptionSchema = z.string().min(1).optional();

export const DEFINITION_WIRE_OPERATIONS = [
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.UpdateDefinition),
    name: nameSchema.describe("The full new title."),
    markdown: markdownSchema.describe(
      "The full new wording, not a diff. Put any SQL in a ```sql block.",
    ),
    description: descriptionSchema.describe(
      "When to use it. Omit to keep the current usage guidance.",
    ),
    scope: wireDefinitionScopeSchema
      .optional()
      .describe("Omit to keep the current scope."),
  }),
  // Its fields are optional, unlike update_definition's: omitted ones keep the assumption's
  // current values, so approving it as it stands names only the assumption.
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.ApproveAssumption),
    name: nameSchema
      .optional()
      .describe("The full new title. Omit to keep the current one."),
    markdown: markdownSchema
      .optional()
      .describe("The full new wording. Omit to keep the current one."),
    description: descriptionSchema.describe(
      "When to use it. Omit to keep the current usage guidance.",
    ),
    scope: wireDefinitionScopeSchema
      .optional()
      .describe("Omit to keep the current scope."),
  }),
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.DeleteDefinition),
  }),
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.CreateDefinition),
    proposed_entity_key: z
      .string()
      .regex(
        PROPOSED_ENTITY_KEY_PATTERN,
        "proposed_entity_key must be a slug of lowercase letters, digits and hyphens (max 64 chars), starting with a letter or digit",
      )
      .describe(
        "Stable slug for this proposed definition, derived from its name. Reuse it to revise your proposal in a later turn.",
      ),
    name: nameSchema.describe("The title."),
    markdown: markdownSchema.describe(
      "The wording. Put any SQL in a ```sql block.",
    ),
    description: descriptionSchema.describe(
      "When to use it. Omit when it applies everywhere.",
    ),
    scope: wireDefinitionScopeSchema,
    stated_by_user: z
      .boolean()
      .optional()
      .describe(
        "True when the user supplied what the definition records. Omit when you worked it out or found it in a context file.",
      ),
  }),
] as const;

// The definition an operation targets is the suggestion's reviewed_entity_id. A create's is the
// assumption it wrote when proposed, or null in a session that writes no assumptions.
export const DEFINITION_STORED_OPERATIONS = [
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.UpdateDefinition),
    name: nameSchema,
    markdown: markdownSchema,
    description: descriptionSchema,
    // Omitted keeps the current scope.
    scope: definitionScopeSchema.optional(),
  }),
  // Stored complete: prepareSuggestion fills what the agent omitted from the served version.
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.ApproveAssumption),
    name: nameSchema,
    markdown: markdownSchema,
    description: descriptionSchema,
    scope: definitionScopeSchema.optional(),
  }),
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.DeleteDefinition),
  }),
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.CreateDefinition),
    name: nameSchema,
    markdown: markdownSchema,
    description: descriptionSchema,
    scope: definitionScopeSchema,
    stated_by_user: z.boolean().optional(),
  }),
] as const;

// The full proposed definition. The definition it changes is the suggestion's
// reviewed_entity_id.
export const updateDefinitionOperationSchema = DEFINITION_STORED_OPERATIONS[0];
